import { FC, Suspense, useEffect, useId, useState } from "react";
import { BuyButtonFormInner } from "../../../../components/common/BuyButton/v2/BuyButtonFormInner";
import { BuyButtonErrorBoundary } from "../../../../components/common/BuyButton/v2/components/BuyButtonErrorBoundary";
import { LoadingSkeleton } from "../../../../components/common/BuyButton/v2/components/LoadingSkeleton";
import { BuyButtonForm } from "../../../../components/common/BuyButton/v2/models/BuyButtonForm";
import { FormSchema } from "../../../../components/common/BuyButton/v2/models/formSchema";
import { getLinkerParameter } from "../../functions/linker";
import { getAdditionalAttributes, useCreateCart } from "../../hooks/useCreateCart";
import { addToCart, viewItem } from "./functions/eventTracker";

type Store = "survaq" | "calqs";

type Props = {
  store: Store;
  formSchema: FormSchema;
  productId: string | null;
  productTitle: string | null;
};

export const BuyButton: FC<Props> = ({ store, formSchema, productId, productTitle }) => {
  const { createCart, isLoading: isCreatingCart } = useCreateCart(store);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isSubmitting = isCreatingCart || isRedirecting;

  // ブラウザバック時にローディング状態をリセット
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setIsRedirecting(false);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const product = productId ? { numericId: productId, title: productTitle ?? "" } : null;

  const onSubmit = async (data: BuyButtonForm) => {
    const item = data.addToCartPayload.items[0];
    if (!item) return;

    // event tracking
    addToCart(data, product, store);

    const checkoutUrl = await createCart({
      merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
      attributes: [
        ...Object.entries(item.properties).map(([key, value]) => ({ key, value })),
        {
          key: "_custom_attributes",
          value: JSON.stringify(
            Object.fromEntries(getAdditionalAttributes().map(({ key, value }) => [key, value])),
          ),
        },
      ],
    });

    setIsRedirecting(true);

    const url = new URL(checkoutUrl);
    const linkerParameter = getLinkerParameter(checkoutUrl);
    url.searchParams.set("_gl", linkerParameter);

    window.location.href = url.toString();
  };

  const id = useId();

  return (
    <BuyButtonErrorBoundary>
      <Suspense fallback={<LoadingSkeleton />}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          id={`buy-button-form-${id}`}
        >
          <BuyButtonFormInner
            store={store}
            formSchema={formSchema}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onAfterInit={(form) => viewItem(form, product, store)} // event tracking
          />
        </form>
      </Suspense>
    </BuyButtonErrorBoundary>
  );
};
